/**
 * Vorauswahl der Musterzeilen nach den Merkmalen der Planungsphase
 * (docs/planungsphase-automatisierung-plan.md, Entscheid E6).
 *
 * Bis Oktober 2026 wählte der Assistent alle 66 Musterzeilen vor — der Plan
 * wurde lang, und Zeilen für Kran oder Gewässer standen auch dort, wo es
 * beides nicht gibt. Jetzt zählt das Merkmal, dreiwertig wie überall in der
 * Planungsphase:
 *
 *   unbedingt   Zeile ohne `merkmale`              → vorgewählt
 *   bejaht      ein Merkmal ist ausdrücklich `true` → vorgewählt
 *   ungeklärt   keines wahr, eines fehlt noch       → vorgewählt, gelb markiert
 *   verneint    alle Merkmale ausdrücklich `false`  → abgewählt, bleibt sichtbar
 *
 * Ungeklärt heißt NICHT nein: sonst fehlte die Asbestzeile genau dann, wenn
 * das Baujahr noch nicht erfasst ist. Die Planlänge sinkt also nur dort, wo
 * ein Merkmal ausdrücklich verneint wurde — das ist gewollt.
 *
 * Ohne Planungsphase (`merkmale === null`) gibt es nichts zu prüfen: alle
 * Zeilen gelten als unbedingt, der Assistent verhält sich wie bisher.
 *
 * Reine Funktion, kein React — die Oberfläche hält nur die Abweichungen des
 * Nutzers von dieser Vorauswahl.
 */

import type { ProjektMerkmale } from '../planungsphase/types';
import type { MusterZeile } from './musterVorlage';

export type VorauswahlStand = 'unbedingt' | 'bejaht' | 'ungeklaert' | 'verneint';

export interface MusterVorauswahlEintrag {
  /** Index in der übergebenen Zeilenliste. */
  index: number;
  gewaehlt: boolean;
  stand: VorauswahlStand;
  /**
   * Die Merkmale, die den Stand tragen: bei `bejaht` die wahren, bei
   * `ungeklaert` die offenen, bei `verneint` alle. Leer bei `unbedingt`.
   */
  merkmale: (keyof ProjektMerkmale)[];
}

/** `undefined` und `null` sind beide „noch nicht beantwortet". */
const istOffen = (wert: unknown): boolean => wert === undefined || wert === null;

export const musterVorauswahl = (
  zeilen: readonly Pick<MusterZeile, 'merkmale'>[],
  merkmale: ProjektMerkmale | null,
): MusterVorauswahlEintrag[] => {
  const werte = (merkmale || {}) as Record<string, unknown>;
  return (zeilen || []).map((zeile, index) => {
    const schluessel = zeile.merkmale || [];
    if (merkmale === null || schluessel.length === 0) {
      return { index, gewaehlt: true, stand: 'unbedingt', merkmale: [] };
    }
    const wahr = schluessel.filter(k => werte[k] === true);
    if (wahr.length) return { index, gewaehlt: true, stand: 'bejaht', merkmale: wahr };
    const offen = schluessel.filter(k => istOffen(werte[k]));
    if (offen.length) return { index, gewaehlt: true, stand: 'ungeklaert', merkmale: offen };
    return { index, gewaehlt: false, stand: 'verneint', merkmale: [...schluessel] };
  });
};

/**
 * Ausdrückliche Wahl des Nutzers je Zeilenindex. Sie wird IMMER gespeichert —
 * auch wenn sie gerade der Vorauswahl entspricht: Wer eine Zeile vor dem
 * Eintreffen der Planungsphase ab- und wieder anwählt, hat sich entschieden;
 * ein später eintreffendes „Nein" darf diese Zeile nicht erneut abwählen.
 */
export type ZeilenWahl = ReadonlyMap<number, boolean>;

/** Neue Wahl mit `wert` für alle `indizes` — die übrigen Einträge bleiben. */
export const mitWahl = (wahl: ZeilenWahl, indizes: readonly number[], wert: boolean): Map<number, boolean> => {
  const next = new Map(wahl);
  indizes.forEach(i => next.set(i, wert));
  return next;
};

/**
 * Wirksame Auswahl: die ausdrückliche Wahl des Nutzers, sonst die Vorauswahl.
 * Gesperrte Zeilen (schon im Plan) sind nie gewählt — auch nicht per Wahl.
 */
export const wirksameAuswahl = (
  vorauswahl: readonly MusterVorauswahlEintrag[],
  wahl: ZeilenWahl,
  gesperrt: (index: number) => boolean,
): Set<number> => {
  const aus = new Set<number>();
  vorauswahl.forEach(e => {
    if (gesperrt(e.index)) return;
    if (wahl.get(e.index) ?? e.gewaehlt) aus.add(e.index);
  });
  return aus;
};

/** Anzahl je Stand — für die Zusammenfassung über der Liste. */
export const zaehleVorauswahl = (eintraege: readonly MusterVorauswahlEintrag[]): Record<VorauswahlStand, number> => {
  const zaehler: Record<VorauswahlStand, number> = { unbedingt: 0, bejaht: 0, ungeklaert: 0, verneint: 0 };
  eintraege.forEach(e => { zaehler[e.stand]++; });
  return zaehler;
};
